import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from '../Tag';
import { CarTopView } from '../icons/CarTopView';
import { BgColorsOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { SectionIcon } from '../SectionIcon';
import { PAINT_TALL_BOX, PAINT_WIDE_BOX } from '../../view/sections';
import { unit } from '../../view/format';

const viewBox = box => `${box.x} ${box.y} ${box.w} ${box.h}`;
const at = ({ left, top }) => ({ left: `${left.toFixed(2)}%`, top: `${top.toFixed(2)}%` });

export const PaintThickness = ({ paint }) => {
    const { t } = useTranslation('reportPage');
    // on a phone the table sits behind "Show details" (always shown on wider screens and in print)
    const [details, setDetails] = useState(false);
    const id = `pt${useId().replace(/:/g, '')}`;
    const tableId = `${id}-table`;

    return (
        <section id="paint" className="card" aria-labelledby="paint-title" style={{ gap: 16 }}>
            <div className="sechead">
                <h2 id="paint-title" className="h2">
                    <SectionIcon icon={BgColorsOutlined} />
                    {t('paint.title')}
                </h2>
                <div className="legend">
                    <span className="lg">
                        <span className="pt-key ok" />
                        {t('paint.legendOriginal')}
                    </span>
                    <span className="lg">
                        <span className="pt-key warn" />
                        {t('paint.legendRepainted')}
                    </span>
                    <span className="lg">
                        <span className="pt-key alert" />
                        {t('paint.legendFiller')}
                    </span>
                    {paint.missing && (
                        <span className="lg">
                            <span className="pt-key none" />
                            {t('paint.legendNone')}
                        </span>
                    )}
                </div>
            </div>
            {paint.pins.length > 0 && (
                <div className="stage" style={{ padding: '20px 24px' }}>
                    {/* wide screens and print: the car lying down, each value with its panel name */}
                    <div
                        className="carmap pt-map pt-wide"
                        style={{ aspectRatio: `${PAINT_WIDE_BOX.w} / ${PAINT_WIDE_BOX.h}` }}
                        data-testid="paint-map-wide"
                    >
                        <svg
                            viewBox={viewBox(PAINT_WIDE_BOX)}
                            width="100%"
                            height="100%"
                            role="img"
                            aria-label={paint.mapAria}
                            style={{ position: 'absolute', inset: 0, display: 'block' }}
                        >
                            <CarTopView places={paint.places} id={`${id}w`} />
                            {paint.pins
                                .filter(pin => pin.lead)
                                .map(pin => (
                                    <g key={pin.key}>
                                        <line
                                            x1={pin.lead[0]}
                                            y1={pin.lead[1]}
                                            x2={pin.lead[2]}
                                            y2={pin.lead[3]}
                                            stroke="rgba(255,255,255,0.4)"
                                            strokeWidth="1.2"
                                            strokeDasharray="3 3"
                                        />
                                        <circle cx={pin.lead[2]} cy={pin.lead[3]} r="4" fill="#FFFFFF" />
                                    </g>
                                ))}
                        </svg>
                        {/* in the corners beside the named values, level with each side's row */}
                        <span className="pt-side" style={at(paint.sideLabels.wide.right)} aria-hidden="true">
                            {t('paint.rightSide')}
                        </span>
                        <span className="pt-side" style={at(paint.sideLabels.wide.left)} aria-hidden="true">
                            {t('paint.leftSide')}
                        </span>
                        {paint.pins.map(pin => (
                            <span
                                className="pt-callout"
                                style={at(pin.wide)}
                                key={pin.key}
                                title={pin.aria}
                                aria-hidden="true"
                            >
                                <span className="pt-name">{pin.name}</span>
                                <span className={`pt-pin ${pin.cls}`}>
                                    {pin.value}
                                    <span className="pt-unit">{unit('um')}</span>
                                </span>
                            </span>
                        ))}
                    </div>
                    {/* phone: the car upright, values only */}
                    <div
                        className="carmap pt-map pt-tall"
                        style={{ aspectRatio: `${PAINT_TALL_BOX.w} / ${PAINT_TALL_BOX.h}` }}
                        data-testid="paint-map-tall"
                    >
                        <svg
                            viewBox={viewBox(PAINT_TALL_BOX)}
                            width="100%"
                            height="100%"
                            role="img"
                            aria-label={paint.mapAria}
                            style={{ position: 'absolute', inset: 0, display: 'block' }}
                        >
                            <g transform="translate(400,0) rotate(90)">
                                <CarTopView places={paint.places} id={`${id}t`} />
                            </g>
                        </svg>
                        <span className="pt-side tall right" style={at(paint.sideLabels.tall.right)} aria-hidden="true">
                            {t('paint.rightSide')}
                        </span>
                        <span className="pt-side tall left" style={at(paint.sideLabels.tall.left)} aria-hidden="true">
                            {t('paint.leftSide')}
                        </span>
                        {paint.pins.map(pin => (
                            <span
                                className={`pt-pin ${pin.cls}`}
                                style={at(pin.tall)}
                                title={pin.aria}
                                key={pin.key}
                                aria-hidden="true"
                            >
                                {pin.value}
                            </span>
                        ))}
                    </div>
                    <div className="stage-legend">
                        <span className="lgc pt-wide-only">{t('paint.viewWide')}</span>
                        <span className="lgc pt-tall-only">{t('paint.viewTall')}</span>
                    </div>
                </div>
            )}
            <button
                type="button"
                className="textlink chips-more pt-details noprint"
                aria-expanded={details}
                aria-controls={tableId}
                onClick={() => setDetails(open => !open)}
            >
                {details ? <UpOutlined /> : <DownOutlined />} {t(details ? 'paint.hideDetails' : 'paint.showDetails')}
            </button>
            <div
                id={tableId}
                className={`gt pt-table${details ? ' shown' : ''}`}
                role="table"
                aria-label={t('paint.tableAria')}
            >
                <div
                    className="gt-row gt-head"
                    role="row"
                    style={{ gridTemplateColumns: 'minmax(160px, 1fr) 120px 120px 170px' }}
                >
                    <span className="gt-cell" role="columnheader">
                        {t('paint.panel')}
                    </span>
                    <span className="gt-cell" role="columnheader">
                        {t('paint.side')}
                    </span>
                    <span className="gt-cell num" role="columnheader">
                        {t('paint.thickness')}
                    </span>
                    <span className="gt-cell" role="columnheader">
                        {t('paint.reading')}
                    </span>
                </div>
                {paint.panels.map((panel, i) => (
                    <div
                        className="gt-row"
                        role="row"
                        style={{ gridTemplateColumns: 'minmax(160px, 1fr) 120px 120px 170px' }}
                        // hood and bonnet can both be “Bonnet, Centre”
                        key={`${i}-${panel.name}-${panel.side}`}
                    >
                        <span className="gt-cell" role="cell">
                            {panel.name}
                        </span>
                        <span className="gt-cell" role="cell">
                            {panel.side}
                        </span>
                        <span className="gt-cell num" role="cell">
                            {panel.um}
                        </span>
                        <span className="gt-cell" role="cell">
                            <Tag tone={panel.tagStyle}>{panel.tag}</Tag>
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
};
